import { useEffect, useMemo, useState } from "react";
import {
  Check, CheckSquare, ChevronDown, ChevronRight, Eye, EyeOff,
  HelpCircle, Loader2, Pencil, Plus, Save, Square, Trash2, X,
} from "lucide-react";

import { useConfirm } from "@/components/confirm-dialog";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  FAQ_CATEGORIES, useBulkDeleteFAQs, useBulkUpdateFAQs, useCreateFAQ,
  useDeleteFAQ, useFAQs, useUpdateFAQ,
  type FAQCategory, type FAQRow,
} from "@/hooks/useFAQs";
import { cn } from "@/lib/utils";

/**
 * /faqs — Admin-only management surface for the public FAQ page
 * (server-rendered at /faq/ on the public site). Mirrors the Gallery
 * page's pattern: header + filter + grouped list + per-row hover
 * actions + multi-select bulk bar + create / edit dialogs.
 *
 * Backed by /api/v1/faqs/ which is locked to Admin role on every action.
 */
export default function FAQs() {
  const list       = useFAQs();
  const create     = useCreateFAQ();
  const update     = useUpdateFAQ();
  const del        = useDeleteFAQ();
  const bulkUpdate = useBulkUpdateFAQs();
  const bulkDelete = useBulkDeleteFAQs();
  const confirm    = useConfirm();

  const [filter, setFilter]   = useState<"all" | FAQCategory>("all");
  const [showCreate, setShowCreate] = useState(false);
  const [editing, setEditing] = useState<FAQRow | null>(null);

  // Selection
  const [selected, setSelected] = useState<Set<number>>(() => new Set());
  const bulkBusy = bulkUpdate.isPending || bulkDelete.isPending;
  function toggleSelect(id: number) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function clearSelection() { setSelected(new Set()); }

  const rows = list.data ?? [];
  const visible = useMemo(() => {
    return filter === "all" ? rows : rows.filter((r) => r.category === filter);
  }, [rows, filter]);

  // Group visible rows by category for the rendered list.
  const grouped = useMemo(() => {
    const map = new Map<FAQCategory, FAQRow[]>();
    for (const r of visible) {
      const arr = map.get(r.category) ?? [];
      arr.push(r);
      map.set(r.category, arr);
    }
    // Sort each bucket by sort_order, then id.
    for (const arr of map.values()) {
      arr.sort((a, b) => (a.sort_order - b.sort_order) || (a.id - b.id));
    }
    // Return in the canonical category order.
    return FAQ_CATEGORIES
      .map(({ code, label }) => ({ code, label, items: map.get(code) ?? [] }))
      .filter(({ items }) => items.length > 0);
  }, [visible]);

  // Track which group panels are expanded. Default: all expanded.
  const [collapsed, setCollapsed] = useState<Set<FAQCategory>>(() => new Set());
  function toggleGroup(code: FAQCategory) {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code); else next.add(code);
      return next;
    });
  }

  const visibleIds = useMemo(() => new Set(visible.map((r) => r.id)), [visible]);
  const allVisibleSelected = visible.length > 0 && visible.every((r) => selected.has(r.id));
  function selectAllVisible() {
    setSelected(new Set([...selected, ...visibleIds]));
  }

  // Trim stale ids after deletes / filter switches.
  useEffect(() => {
    if (selected.size === 0) return;
    const existing = new Set(rows.map((r) => r.id));
    const trimmed = new Set([...selected].filter((id) => existing.has(id)));
    if (trimmed.size !== selected.size) setSelected(trimmed);
  }, [rows, selected]);

  // Esc clears selection.
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape" && selected.size > 0 && !bulkBusy) clearSelection();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selected.size, bulkBusy]);

  const stats = useMemo(() => ({
    total:  rows.length,
    active: rows.filter((r) => r.is_active).length,
    hidden: rows.filter((r) => !r.is_active).length,
  }), [rows]);

  async function onDelete(row: FAQRow) {
    const ok = await confirm({
      title: "Delete this FAQ?",
      description:
        `"${row.question.slice(0, 90)}${row.question.length > 90 ? "…" : ""}" will be ` +
        "removed from the public FAQ page immediately. This cannot be undone.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    del.mutate(row.id);
  }

  async function onBulkPatch(patch: Partial<Pick<FAQRow, "is_active">>) {
    if (selected.size === 0) return;
    await bulkUpdate.mutateAsync({ ids: [...selected], patch });
    clearSelection();
  }
  async function onBulkDelete() {
    if (selected.size === 0) return;
    const ok = await confirm({
      title: `Delete ${selected.size} FAQ${selected.size === 1 ? "" : "s"}?`,
      description:
        "Selected questions will be removed from the public FAQ page " +
        "immediately. This cannot be undone.",
      confirmLabel: `Delete ${selected.size}`,
      destructive: true,
    });
    if (!ok) return;
    await bulkDelete.mutateAsync([...selected]);
    clearSelection();
  }

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      {/* ---------- Header ---------- */}
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <div className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.16em] text-muted-foreground">
            <HelpCircle className="h-3.5 w-3.5" />Public FAQ
          </div>
          <h1 className="mt-1 text-3xl font-bold tracking-tight">FAQs</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Questions &amp; answers for{" "}
            <code className="rounded bg-muted px-1 py-0.5 text-xs">/faq/</code>{" "}
            on the public site. Changes are visible immediately. Groups
            and ordering match the public page.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)} className="shrink-0">
          <Plus className="mr-2 h-4 w-4" />Add FAQ
        </Button>
      </header>

      {/* ---------- Stats + filter ---------- */}
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <StatPill label="Total" value={stats.total} />
        <StatPill label="Visible" value={stats.active} accent="text-emerald-600" />
        <StatPill label="Hidden" value={stats.hidden} accent="text-muted-foreground" />
        <div className="ml-auto flex flex-wrap items-center gap-1 rounded-md border border-border/60 bg-card p-1">
          <FilterChip current={filter} value="all" onClick={setFilter}>All</FilterChip>
          {FAQ_CATEGORIES.map(({ code, label }) => (
            <FilterChip key={code} current={filter} value={code} onClick={setFilter}>
              {label.split(" ")[0]}
            </FilterChip>
          ))}
        </div>
      </div>

      {/* ---------- Bulk action bar (sticky when any selected) ---------- */}
      {selected.size > 0 && (
        <div className="sticky top-0 z-30 -mx-2 flex flex-wrap items-center gap-2 rounded-lg border border-primary/30 bg-primary/10 px-3 py-2 shadow-soft backdrop-blur supports-[backdrop-filter]:bg-primary/10">
          <div className="flex items-center gap-2 text-sm font-medium text-primary">
            <CheckSquare className="h-4 w-4" />
            {selected.size} selected
          </div>
          <Button variant="ghost" size="sm" onClick={selectAllVisible} disabled={bulkBusy || allVisibleSelected} className="h-7">
            Select all visible
          </Button>
          <div className="mx-1 hidden h-5 w-px bg-border/60 sm:block" />
          <Button variant="outline" size="sm" onClick={() => onBulkPatch({ is_active: true })}  disabled={bulkBusy} className="h-7">
            <Eye className="mr-1.5 h-3.5 w-3.5" />Show
          </Button>
          <Button variant="outline" size="sm" onClick={() => onBulkPatch({ is_active: false })} disabled={bulkBusy} className="h-7">
            <EyeOff className="mr-1.5 h-3.5 w-3.5" />Hide
          </Button>
          <Button
            variant="outline" size="sm"
            onClick={onBulkDelete} disabled={bulkBusy}
            className="h-7 border-destructive/40 text-destructive hover:bg-destructive/10 hover:text-destructive"
          >
            <Trash2 className="mr-1.5 h-3.5 w-3.5" />Delete
          </Button>
          <Button variant="ghost" size="sm" onClick={clearSelection} disabled={bulkBusy} className="ml-auto h-7">
            {bulkBusy
              ? <><Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />Working…</>
              : <><X className="mr-1.5 h-3.5 w-3.5" />Clear</>}
          </Button>
        </div>
      )}

      {/* ---------- List ---------- */}
      {list.isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-16 w-full" />)}
        </div>
      ) : grouped.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center gap-3 py-16 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-muted">
              <HelpCircle className="h-5 w-5 text-muted-foreground" />
            </div>
            <h3 className="text-sm font-semibold">
              {filter === "all" ? "No FAQs yet" : "No FAQs in this category"}
            </h3>
            <p className="max-w-sm text-xs text-muted-foreground">
              {filter === "all"
                ? "Add your first question. The public /faq/ page will start showing it instantly."
                : "Switch the filter back to All, or add one in this category."}
            </p>
            {filter === "all" && (
              <Button onClick={() => setShowCreate(true)} className="mt-2">
                <Plus className="mr-2 h-4 w-4" />Add FAQ
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {grouped.map(({ code, label, items }) => {
            const isCollapsed = collapsed.has(code);
            return (
              <Card key={code} className="border-border/60 shadow-soft">
                <button
                  type="button"
                  onClick={() => toggleGroup(code)}
                  className="flex w-full items-center gap-2 px-4 py-3 text-left transition-colors hover:bg-muted/50"
                >
                  {isCollapsed
                    ? <ChevronRight className="h-4 w-4 text-muted-foreground" />
                    : <ChevronDown  className="h-4 w-4 text-muted-foreground" />}
                  <span className="text-sm font-semibold uppercase tracking-[0.08em] text-foreground">
                    {label}
                  </span>
                  <Badge variant="outline" className="ml-1 font-mono text-[10px]">
                    {items.length}
                  </Badge>
                </button>
                {!isCollapsed && (
                  <ul className="divide-y divide-border/60 border-t border-border/60">
                    {items.map((row) => (
                      <FAQRowItem
                        key={row.id}
                        row={row}
                        isSelected={selected.has(row.id)}
                        anySelected={selected.size > 0}
                        onToggleSelect={() => toggleSelect(row.id)}
                        onEdit={() => setEditing(row)}
                        onDelete={() => onDelete(row)}
                        onToggleActive={() => update.mutate({ id: row.id, is_active: !row.is_active })}
                        busy={update.isPending || del.isPending || bulkBusy}
                      />
                    ))}
                  </ul>
                )}
              </Card>
            );
          })}
        </div>
      )}

      {/* ---------- Dialogs ---------- */}
      <FAQDialog
        open={showCreate}
        onOpenChange={setShowCreate}
        initial={null}
        onSubmit={(input) => create.mutate(input as never, { onSuccess: () => setShowCreate(false) })}
        busy={create.isPending}
        error={extractError(create.error)}
      />
      <FAQDialog
        open={!!editing}
        onOpenChange={(v) => !v && setEditing(null)}
        initial={editing}
        onSubmit={(input) => {
          if (!editing) return;
          update.mutate({ id: editing.id, ...input }, { onSuccess: () => setEditing(null) });
        }}
        busy={update.isPending}
        error={extractError(update.error)}
      />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Stat pill + filter chip — same shape as the Gallery page so the two
// admin surfaces feel like one product.
// ---------------------------------------------------------------------------
function StatPill({ label, value, accent }: { label: string; value: number; accent?: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-md border border-border/60 bg-card px-2.5 py-1 text-xs">
      <span className="text-muted-foreground">{label}</span>
      <span className={cn("font-semibold tabular-nums", accent ?? "text-foreground")}>{value}</span>
    </span>
  );
}

function FilterChip<T extends string>({
  current, value, onClick, children,
}: {
  current: T;
  value: T;
  onClick: (v: T) => void;
  children: React.ReactNode;
}) {
  const active = current === value;
  return (
    <button
      type="button"
      onClick={() => onClick(value)}
      className={cn(
        "rounded px-2.5 py-1 text-xs font-medium transition-colors",
        active
          ? "bg-primary text-primary-foreground"
          : "text-muted-foreground hover:bg-accent hover:text-accent-foreground",
      )}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Row
// ---------------------------------------------------------------------------
function FAQRowItem({
  row, isSelected, anySelected, onToggleSelect,
  onEdit, onDelete, onToggleActive, busy,
}: {
  row: FAQRow;
  isSelected: boolean;
  anySelected: boolean;
  onToggleSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onToggleActive: () => void;
  busy: boolean;
}) {
  return (
    <li className={cn(
      "group flex items-start gap-3 px-4 py-3 transition-colors hover:bg-muted/30",
      !row.is_active && "opacity-60",
      isSelected && "bg-primary/5",
    )}>
      {/* Checkbox */}
      <button
        type="button"
        onClick={onToggleSelect}
        aria-label={isSelected ? "Deselect FAQ" : "Select FAQ"}
        aria-pressed={isSelected}
        disabled={busy}
        title={isSelected ? "Deselect" : "Select"}
        className={cn(
          "mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded border transition-colors",
          isSelected
            ? "border-primary bg-primary text-primary-foreground"
            : "border-border/70 bg-card text-muted-foreground hover:border-primary/50",
          anySelected ? "opacity-100" : "opacity-0 group-hover:opacity-100",
          busy && "cursor-not-allowed opacity-50",
        )}
      >
        {isSelected
          ? <Check  className="h-3 w-3" />
          : <Square className="h-3 w-3 stroke-[1.6]" />}
      </button>

      {/* Question + answer preview */}
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <h4 className="text-sm font-semibold text-foreground">{row.question}</h4>
          {!row.is_active && (
            <Badge variant="outline" className="border-foreground/30 text-foreground">
              Hidden
            </Badge>
          )}
          <span className="ml-auto text-[10px] font-mono text-muted-foreground">
            #{row.sort_order}
          </span>
        </div>
        <p className="mt-1 line-clamp-2 text-xs text-muted-foreground">
          {row.answer}
        </p>
      </div>

      {/* Hover actions */}
      <div className="flex shrink-0 items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100">
        <IconBtn
          onClick={onToggleActive}
          title={row.is_active ? "Hide from public site" : "Show on public site"}
          disabled={busy}
        >
          {row.is_active
            ? <Eye    className="h-3.5 w-3.5" />
            : <EyeOff className="h-3.5 w-3.5" />}
        </IconBtn>
        <IconBtn onClick={onEdit} title="Edit FAQ" disabled={busy}>
          <Pencil className="h-3.5 w-3.5" />
        </IconBtn>
        <IconBtn onClick={onDelete} title="Delete FAQ" disabled={busy} variant="destructive">
          <Trash2 className="h-3.5 w-3.5" />
        </IconBtn>
      </div>
    </li>
  );
}

function IconBtn({
  children, onClick, title, disabled, variant,
}: {
  children: React.ReactNode;
  onClick: () => void;
  title: string;
  disabled?: boolean;
  variant?: "destructive";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      aria-label={title}
      disabled={disabled}
      className={cn(
        "flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-foreground",
        variant === "destructive" && "hover:bg-destructive/10 hover:text-destructive",
        disabled && "cursor-not-allowed opacity-50",
      )}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Create / Edit dialog (shared)
// ---------------------------------------------------------------------------
interface FAQDialogInput {
  question: string;
  answer: string;
  category: FAQCategory;
  sort_order: number;
  is_active: boolean;
}

function FAQDialog({
  open, onOpenChange, initial, onSubmit, busy, error,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  initial: FAQRow | null;
  onSubmit: (input: FAQDialogInput) => void;
  busy: boolean;
  error: string | null;
}) {
  const isEdit = !!initial;
  const [question, setQuestion] = useState("");
  const [answer, setAnswer]     = useState("");
  const [category, setCategory] = useState<FAQCategory>("general");
  const [sortOrder, setSortOrder] = useState(0);
  const [active, setActive]     = useState(true);

  // Reseed every time the dialog opens / the editing target changes.
  useEffect(() => {
    if (!open) return;
    setQuestion(initial?.question ?? "");
    setAnswer(initial?.answer ?? "");
    setCategory(initial?.category ?? "general");
    setSortOrder(initial?.sort_order ?? 0);
    setActive(initial?.is_active ?? true);
  }, [open, initial]);

  const canSubmit = question.trim().length >= 4 && answer.trim().length >= 4;

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!canSubmit) return;
    onSubmit({
      question: question.trim(),
      answer: answer.trim(),
      category,
      sort_order: sortOrder,
      is_active: active,
    });
  }

  return (
    <Dialog open={open} onOpenChange={(v) => !busy && onOpenChange(v)}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? "Edit FAQ" : "Add FAQ"}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? "Changes go live on the public /faq/ page immediately."
              : "New FAQs appear on the public /faq/ page as soon as you save."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <Label htmlFor="faq-question">Question</Label>
            <Input
              id="faq-question"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder="e.g. How do I access historical KPI data?"
              maxLength={400}
              disabled={busy}
              autoFocus
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="faq-answer">Answer</Label>
            <Textarea
              id="faq-answer"
              value={answer}
              onChange={(e) => setAnswer(e.target.value)}
              placeholder="Plain text or basic HTML. Use line breaks for paragraphs."
              rows={6}
              disabled={busy}
            />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="faq-category">Category</Label>
              <select
                id="faq-category"
                value={category}
                onChange={(e) => setCategory(e.target.value as FAQCategory)}
                disabled={busy}
                className="flex h-9 w-full rounded-md border border-border/60 bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
              >
                {FAQ_CATEGORIES.map(({ code, label }) => (
                  <option key={code} value={code}>{label}</option>
                ))}
              </select>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="faq-sort">Sort order</Label>
              <Input
                id="faq-sort"
                type="number"
                min={0}
                value={sortOrder}
                onChange={(e) => setSortOrder(Math.max(0, parseInt(e.target.value || "0", 10) || 0))}
                disabled={busy}
              />
              <p className="text-[11px] text-muted-foreground">
                Lower numbers show first within the category.
              </p>
            </div>
          </div>

          <label className="flex cursor-pointer items-center gap-2 rounded-md border border-border/60 bg-card px-3 py-2 text-sm hover:bg-accent">
            <input
              type="checkbox"
              checked={active}
              onChange={(e) => setActive(e.target.checked)}
              disabled={busy}
              className="h-4 w-4 rounded border-border/60 text-primary focus:ring-2 focus:ring-primary/30"
            />
            {active ? <Eye className="h-3.5 w-3.5" /> : <EyeOff className="h-3.5 w-3.5" />}
            <span>{active ? "Visible on the public page" : "Hidden (not shown to visitors)"}</span>
          </label>

          {error && (
            <p className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-xs text-destructive">
              {error}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)} disabled={busy}>
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit || busy}>
              {busy
                ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" />Saving…</>
                : <><Save     className="mr-2 h-4 w-4" />{isEdit ? "Save changes" : "Add FAQ"}</>}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------
// Error extraction helper (same shape as Gallery.tsx)
// ---------------------------------------------------------------------------
function extractError(err: unknown): string | null {
  if (!err) return null;
  const e = err as { response?: { data?: { detail?: string; question?: string[]; answer?: string[] } } };
  return (
    e.response?.data?.detail
    || e.response?.data?.question?.[0]
    || e.response?.data?.answer?.[0]
    || (err as Error).message
    || "Something went wrong."
  );
}
